import React, { createContext, useContext, useState, useCallback } from 'react';
import type { AdminLesson } from '@/hooks/useAdminCourse';

interface LessonEditorContextType {
  isOpen: boolean;
  moduleId: string | null;
  lesson: AdminLesson | null;
  nextSortOrder: number;
  openEditor: (moduleId: string, lesson?: AdminLesson | null, nextSortOrder?: number) => void;
  closeEditor: () => void;
  setModuleId: (moduleId: string | null) => void;
  setLesson: (lesson: AdminLesson | null) => void;
  setNextSortOrder: (order: number) => void;
}

const LessonEditorContext = createContext<LessonEditorContextType | undefined>(undefined);

export function LessonEditorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [moduleId, setModuleIdState] = useState<string | null>(null);
  const [lesson, setLessonState] = useState<AdminLesson | null>(null);
  const [nextSortOrder, setNextSortOrderState] = useState<number>(0);

  const openEditor = useCallback((moduleId: string, lesson?: AdminLesson | null, nextSortOrder?: number) => {
    setModuleIdState(moduleId);
    setLessonState(lesson || null);
    if (nextSortOrder !== undefined) {
      setNextSortOrderState(nextSortOrder);
    }
    setIsOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
    // Don't reset moduleId, lesson, or nextSortOrder to maintain state
  }, []);

  const setModuleId = useCallback((moduleId: string | null) => {
    setModuleIdState(moduleId);
  }, []);

  const setLesson = useCallback((lesson: AdminLesson | null) => {
    setLessonState(lesson);
  }, []);

  const setNextSortOrder = useCallback((order: number) => {
    setNextSortOrderState(order);
  }, []);

  return (
    <LessonEditorContext.Provider
      value={{
        isOpen,
        moduleId,
        lesson,
        nextSortOrder,
        openEditor,
        closeEditor,
        setModuleId,
        setLesson,
        setNextSortOrder,
      }}
    >
      {children}
    </LessonEditorContext.Provider>
  );
}

export function useLessonEditor() {
  const context = useContext(LessonEditorContext);
  if (!context) {
    throw new Error('useLessonEditor must be used within LessonEditorProvider');
  }
  return context;
}
